<template>
  <div class="roadtrip">
    <div class="container">
      <div class="roadtrip__loading">
        <h1>We are planning your perfect trip...</h1>

        <img v-if="desktop" src="/Static/images/roadtrip/loading_desktop.gif" alt="" class="roadtrip__loading-img" />
        <img v-else src="/Static/images/roadtrip/loading_mobile.gif" alt="" class="roadtrip__loading-img" />

        <div class="roadtrip__loading-bar">
          <span class="roadtrip__loading-colour"></span>
        </div>
      </div>
    </div>
    <div class="roadtrip__final-page">
      <div class="roadtrip-recommendations" v-if="recommendedActivities.length > 0">
        <div class="roadtrip-banner">
          <span class="roadtrip-banner__map"></span>

          <div class="container">
            <div class="roadtrip-banner__text">
              <h1>Your personalised adventure planner</h1>
              <p class="roadtrip__subtitle">
                Here's all the attractions we think you'll love based on the answers you gave:
              </p>
              <div class="roadtrip-choices">
                <div
                  class="roadtrip-choices__choice"
                  v-for="(value, name) in selectedAnswers"
                  :key="name"
                  :class="{
                    'roadtrip-choices__choice--hidden': value === 'England' && selectedAnswers.county,
                  }"
                >
                  <span class="roadtrip-choices__choice-img">
                    <img :src="'/Static/images/roadtrip/icons/' + value.replace(/\s/g, '-') + '.svg'" :alt="value" />
                  </span>
                  <span class="roadtrip-choices__choice-text">
                    <template v-if="value === 'Couple'"> Group </template>
                    <template v-else> {{ value }}</template></span
                  >
                </div>
              </div>

              <p class="covid">
                Please help to keep yourself and others safe by checking
                <a href="https://www.gov.uk/coronavirus" target="_blank">government guidance around COVID-19</a> for
                local advice before travelling anywhere as restrictions may differ.
              </p>
            </div>
          </div>
        </div>
        <div class="container">
          <div class="roadtrip-recommendation-wrapper">
            <div class="roadtrip-recommendation" v-for="location in recommendedActivities" :key="location.id">
              <div class="roadtrip-recommendation__inner">
                <div class="roadtrip-recommendation__image">
                  <img :src="'/Static/images/roadtrip/locations/' + location.image" :alt="location.name" />
                </div>
                <div class="roadtrip-recommendation__text">
                  <h3>{{ location.name }}</h3>
                  <p>{{ location.tags.description.join(', ') }}</p>

                  <div class="roadtrip-recommendation__features">
                    <div class="roadtrip-recommendation__feature">
                      <template v-if="location.tags.pets.join(', ') === 'Pet friendly'">
                        <span class="roadtrip-recommendation__feature-icon"
                          ><img src="/Static/images/roadtrip/icons/small-icons/pets.svg" alt="Pets"
                        /></span>
                        <span class="roadtrip-recommendation__feature-text">Dog friendly</span>
                      </template>
                      <template v-else>
                        <span class="roadtrip-recommendation__feature-icon"
                          ><img src="/Static/images/roadtrip/icons/small-icons/nopets.svg" alt="No Pets"
                        /></span>
                        <span class="roadtrip-recommendation__feature-text">No pets</span>
                      </template>
                    </div>
                    <div class="roadtrip-recommendation__feature">
                      <template v-if="location.tags.who.includes(selectedAnswers.who)">
                        <span class="roadtrip-recommendation__feature-icon"
                          ><img
                            :src="
                              '/Static/images/roadtrip/icons/small-icons/' +
                              selectedAnswers.who.replace(' ', '') +
                              '.svg'
                            "
                            :alt="selectedAnswers.who"
                        /></span>
                        <span class="roadtrip-recommendation__feature-text">
                          <template v-if="selectedAnswers.who === 'Couple'"> Group </template>
                          <template v-else>
                            {{ selectedAnswers.who }}
                          </template>
                        </span>
                      </template>
                      <template v-else>
                        <span class="roadtrip-recommendation__feature-icon"
                          ><img
                            :src="
                              '/Static/images/roadtrip/icons/small-icons/' +
                              location.tags.who[0].replace(' ', '') +
                              '.svg'
                            "
                            :alt="location.tags.who[0]"
                        /></span>
                        <span class="roadtrip-recommendation__feature-text">{{ location.tags.who[0] }}</span>
                      </template>
                    </div>
                    <div class="roadtrip-recommendation__feature">
                      <template
                        v-if="
                          location.tags.budget.join(', ') === 'Freebies only' ||
                          location.tags.budget.join(', ') === 'Freebies only, I have some spare pennies'
                        "
                      >
                        <span class="roadtrip-recommendation__feature-icon"
                          ><img src="/Static/images/roadtrip/icons/small-icons/cashsmall.svg" alt="Free"
                        /></span>
                        <span class="roadtrip-recommendation__feature-text">Free</span>
                      </template>
                      <template v-else>
                        <span class="roadtrip-recommendation__feature-icon"
                          ><img src="/Static/images/roadtrip/icons/small-icons/expensive.svg" alt="paid"
                        /></span>
                        <span class="roadtrip-recommendation__feature-text">Paid</span>
                      </template>
                    </div>
                    <div class="roadtrip-recommendation__feature">
                      <template v-if="location.tags.tripLength.includes(selectedAnswers.tripLength)">
                        <span class="roadtrip-recommendation__feature-icon"
                          ><img
                            :src="
                              '/Static/images/roadtrip/icons/small-icons/' +
                              selectedAnswers.tripLength.replace(' ', '') +
                              '.svg'
                            "
                            :alt="selectedAnswers.tripLength"
                        /></span>
                        <span class="roadtrip-recommendation__feature-text">{{ selectedAnswers.tripLength }}</span>
                      </template>
                      <template v-else>
                        <span class="roadtrip-recommendation__feature-icon"
                          ><img
                            :src="
                              '/Static/images/roadtrip/icons/small-icons/' +
                              location.tags.tripLength[0].replace(' ', '') +
                              '.svg'
                            "
                            :alt="location.tags.tripLength[0]"
                        /></span>
                        <span class="roadtrip-recommendation__feature-text">
                          <template v-if="location.tags.tripLength[0] === 'All day'"> Day Trip </template>
                          <template v-else-if="location.tags.tripLength[0] === 'Short'"> Up to 3 days </template>
                          <template v-else> Up to 7 days </template>
                        </span>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <p v-if="link">
          <a :href="link" class="arrow-link" rel="noreferrer">Tell me more about activities in this region</a>
        </p>

        <div class="roadtrip__recommend-buttons">
          <a :href="insuranceLink" class="btn" target="_blank" rel="noreferrer"
            >Get ready to go with insurance for your vehicle</a
          >
          <button type="button" @click="startAgain" class="btn btn--black-border">Start again</button>
        </div>
      </div>
      <div v-else>
        <div class="roadtrip__no-results">
          <img src="/Static/images/roadtrip/logo.gif" alt="Adventure Planner" class="roadtrip__logo" />
          <p>Oh no - we couldn't find any results to match your answers!</p>
          <p>Try widening your search or hit our 'Surprise Me' button to see where the open road could take you...</p>

          <div class="roadtrip__recommend-buttons">
            <button type="button" @click="startAgain" class="btn btn--black-border btn--center">Start again</button>
            <button type="button" class="btn" @click="addItems">Surprise Me</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'Recommendation',
  data: () => ({
    desktop: false,
  }),

  computed: {
    ...mapState('roadtrip', ['step', 'suggestions', 'link']),
    ...mapGetters('roadtrip', ['selectedAnswers', 'recommendedActivities']),

    insuranceLink() {
      // Sets the insurance link to match the travel selected, if not selected it uses the first one in the list
      if (this.selectedAnswers.transport) {
        for (let index = 0; index < this.insuranceUrls.length; index++) {
          const element = this.insuranceUrls[index];
          if (element.id.indexOf(this.selectedAnswers.transport) >= 0) {
            return element.url;
          }
        }
      }
      return this.defaultInsurance;
    },
  },
  props: {
    defaultInsurance: {
      type: String,
      default: 'https://money.asda.com/insurance/',
    },
    insuranceUrls: {
      type: Array,
      default: () => [],
    },
  },

  created() {
    if (window.innerWidth > 768) {
      this.desktop = true;
    }

    setTimeout(function () {
      document.querySelector('.roadtrip').classList.add('roadtrip--recommendation-page');
    }, 4000);
  },
  methods: {
    startAgain() {
      location.reload();
    },

    addItems() {
      this.$store.commit('roadtrip/setCountryOnly');
    },
  },
};
</script>

<style scoped lang="scss">
.roadtrip {
  min-height: 50vh;
  position: relative;
}

.roadtrip-banner {
  padding: 24rem 0 1.5rem;
  position: relative;

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 1;
    display: block;
    width: 100%;
    height: calc(100% - 237px);
    background: $white;
  }
  &::after {
    content: '';
    top: 180px;
    display: block;
    width: 100%;
    height: 112px;
    position: absolute;
    bottom: 0;
    left: 0;
    z-index: 2;
    background: linear-gradient(9deg, $white 0%, $white 50%, rgba(255, 255, 255, 0) 50%, rgba(255, 255, 255, 0) 100%);
  }

  @media only screen and (min-width: 992px) {
    padding: 5rem 0 1.5rem;
    margin-bottom: 5rem;
    position: relative;

    &::before {
      top: 0;
      bottom: auto;
      display: block;
      width: 100%;
      height: 100%;
      background: linear-gradient(
        70deg,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 1) 60%,
        rgba(255, 255, 255, 0) 50%,
        rgba(255, 255, 255, 0) 100%
      );
    }

    &::after {
      display: none;
    }

    .container {
      display: flex;
      width: 100%;
    }
  }

  @media (min-width: 1200px) {
    &::before {
      background: linear-gradient(
        70deg,
        rgba(255, 255, 255, 1) 0%,
        rgba(255, 255, 255, 1) 50%,
        rgba(255, 255, 255, 0) 50%,
        rgba(255, 255, 255, 0) 100%
      );
    }
  }

  .roadtrip-banner__map {
    background: $grey-light url('~/images/roadtrip/mobile-only/map.png') center top no-repeat;
    background-size: auto 237px;
    position: absolute;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    display: block;
    z-index: 0;

    @media only screen and (min-width: 992px) {
      background: $grey-light url('~/images/roadtrip/map.png') no-repeat;
      background-position: right -247px center;
      width: 89%;
      background-size: contain;
    }

    @media only screen and (min-width: 1200px) {
      background-position: right -120px center;
      width: 74%;
    }

    @media only screen and (min-width: 1500px) {
      background-position: right center;
      width: 60%;
    }
  }

  .container {
    position: relative;
    z-index: 3;
  }
}

.roadtrip-wrapper h1 {
  font-size: 2.4rem;
  line-height: 3.2rem;
  margin-bottom: 2rem;

  @media only screen and (min-width: 992px) {
    font-size: 4.8rem;
    line-height: 6.4rem;
    margin-bottom: 0.5rem;
  }
}

.roadtrip-wrapper .roadtrip__subtitle {
  font-weight: 500;
  line-height: 2.8rem;
  margin-bottom: 1.6rem;

  @media only screen and (min-width: 992px) {
    margin-bottom: 3rem;
  }
}

.roadtrip-banner__text {
  @media only screen and (max-width: 767px) {
    margin-top: 2.4rem;

    a {
      word-break: break-all;
    }

    p {
      font-size: 1.6rem;
      line-height: 2.4rem;
    }

    .covid {
      display: none;
    }
  }

  @media only screen and (min-width: 992px) {
    text-align: left;
    flex-basis: 50%;
  }

  @media only screen and (min-width: 1200px) {
    flex-basis: 40%;
  }
}

.roadtrip-banner-map {
  display: none;

  @media only screen and (min-width: 992px) {
    flex-basis: 50%;
    margin-left: auto;
    order: 1;
    display: block;

    img {
      margin-left: auto;
      max-width: 80%;
    }
  }
}

.roadtrip-choices {
  display: flex;
  width: 100%;
  margin-bottom: 1rem;
  flex-wrap: wrap;
}

.roadtrip-choices__choice {
  display: flex;
  width: 50%;
  align-items: center;
  margin-bottom: 2rem;
  padding-right: 2rem;
}

.roadtrip-choices__choice--hidden {
  display: none;
}

.roadtrip-choices__choice-img {
  width: 4rem;
  height: 4rem;
  margin-right: 0.7rem;
  min-width: 4rem;

  @media only screen and (min-width: 768px) {
    width: 6rem;
    min-width: 6rem;
    margin-right: 2rem;
    height: 6rem;
  }
}

.roadtrip-choices__choice-text {
  font-size: 1.4rem;
  line-height: 1.8rem;
  font-weight: 700;
  text-align: left;

  @media only screen and (min-width: 768px) {
    line-height: 2.8rem;
    font-size: 2rem;
  }
}

ul {
  display: inline-block;
}

.roadtrip__no-results {
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 50vh;
  position: relative;
  text-align: center;
  flex-wrap: wrap;
  padding: 5rem 0;

  @media only screen and (min-width: 768px) {
    padding: 5rem 0;
  }

  @media only screen and (min-width: 992px) {
    padding: 7.5rem 0;
  }

  p {
    display: block;
    width: 100%;
  }

  .roadtrip__recommend-buttons > button {
    margin: 0 1rem 2rem;
  }
  .roadtrip__recommend-buttons {
    margin: 3.2rem auto;
  }
}

.roadtrip__final-page {
  opacity: 0;
  animation-name: fadeIn;
  animation-duration: 1s;
  animation-delay: 4s;
  animation-fill-mode: forwards;
  min-height: calc(100vh - 56px);
}

.roadtrip__loading {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 10;
  align-items: center;
  padding: 3rem 0;

  @media only screen and (min-width: 992px) {
    padding: 7.5rem 0;
  }

  animation-name: fadeOut;
  animation-duration: 1s;
  animation-delay: 4s;
  animation-fill-mode: forwards;
}

.roadtrip__loading-img {
  margin: 3rem auto 0;
}

.roadtrip__loading-bar {
  width: 100%;
  max-width: calc(100% - 4.8rem);
  margin: 5.4rem auto 0;
  border: 4px solid $black;
  height: 6.4rem;
  position: relative;
  overflow: hidden;

  @media only screen and (min-width: 992px) {
    max-width: 558px;
  }
}

.roadtrip__loading-colour {
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  background: url('~/images/roadtrip/loader.png') repeat-x left;
  border-right: 4px solid $black;
  width: 0;
  animation-name: loading;
  animation-duration: 5s;
  animation-fill-mode: forwards;
}

@keyframes loading {
  from {
    width: 0;
  }
  to {
    width: 102%;
  }
}

@keyframes fadeOut {
  from {
    opacity: 1;
    z-index: 10;
  }
  to {
    opacity: 0;
    z-index: -10;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.roadtrip-recommendation-wrapper {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  justify-content: center;
  text-align: center;
}

.roadtrip-recommendation {
  flex-basis: 100%;
  margin: 0 auto 30px;

  @media only screen and (min-width: 500px) {
    flex-basis: 50%;
    padding: 0 12px;
  }

  @media only screen and (min-width: 992px) {
    flex-basis: 33%;
  }
}

// If only 1 or 2 items
.roadtrip-recommendation:nth-last-child(n + 1):nth-last-child(-n + 2):first-child,
.roadtrip-recommendation:nth-last-child(n + 1):nth-last-child(-n + 2):first-child ~ .roadtrip-recommendation {
  @media only screen and (min-width: 992px) {
    flex-basis: 50%;
  }
}

.roadtrip-recommendation__inner {
  border: 4px solid $black;
  border-radius: 10px;
  overflow: hidden;
  display: flex;
  height: 100%;
  flex-flow: column;
}

.roadtrip-recommendation__image {
  border-bottom: 4px solid $black;
  align-self: flex-start;
  width: 100%;

  img {
    width: 100%;
  }
}

.roadtrip-recommendation__text {
  text-align: left;
  padding: 2rem;
  flex: 1;
  display: flex;
  flex-direction: column;

  h3 {
    font-weight: 700;
    font-size: 2.4rem;
    line-height: 2.4rem;
    margin-bottom: 2rem;
    color: $black;
  }
}

.roadtrip-recommendation__features {
  margin-top: auto;

  @media only screen and (min-width: 768px) {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
  }
}

.roadtrip-recommendation__feature {
  padding-right: 1rem;
  margin-bottom: 2rem;
  display: flex;
  align-items: center;

  @media only screen and (min-width: 768px) {
    flex-basis: 50%;
    width: 50%;
  }
}
.roadtrip-recommendation__feature-icon {
  margin-right: 1rem;

  @media only screen and (min-width: 768px) {
    min-width: 3rem;
    flex-basis: 3rem;
  }

  @media only screen and (min-width: 992px) {
    flex-basis: 4rem;
    min-width: 4rem;
  }
}

.roadtrip-recommendation__feature-text {
  font-weight: 600;
  line-height: 1.8rem;
  font-size: 1.4rem;
  @media only screen and (min-width: 768px) {
    line-height: 2.4rem;
  }
}

.arrow-link {
  color: $black;
}

.roadtrip__recommend-buttons {
  margin: 3.2rem 0 6rem;

  @media only screen and (max-width: 767px) {
    .btn {
      display: block;
      width: 100%;
      max-width: 32rem;
      margin: 0 auto 2.4rem;
    }
  }
}

.btn--black-border {
  margin-left: 2rem;
}
</style>
